import { defineStore } from 'pinia'
import { computed, watch } from 'vue'
import { createSyncedRows, type RemoteSettings } from '../services/syncedRows'
import { deviceId, useWritingLogStore } from './writingLog'
import { writingDayKey } from '../utils/writingDays'
import { useSettingsStore } from './settings'
import { useCorpusStore } from './corpus'
import {
  blankTask, parseList, parseLogbookEntry, parseTask, taskFromMilestone,
  MILESTONES_LIST_ID, WRITING_LIST_ID,
  type DocketList, type DocketTask, type LogbookEntry,
} from '../utils/docketModel'
import { nextTaskDates } from '../utils/docketDates'
import { nextPrepDates } from '../utils/prepTasks'
import { useCalendarStore } from './calendar'

// Docket's tasks and lists sync one row each (see syncedRows). Finished tasks
// stay as rows for two weeks, so they can be unticked, then move into the
// Logbook: one row per device per month, so a row only ever has one writer.
// A repeating task stays one row; each finish goes to the Logbook at once.

/** How long a finished task stays a row before it moves to the Logbook. */
export const PACK_AFTER_DAYS = 14
const DAY_MS = 86_400_000

interface LogbookMonth {
  entries: LogbookEntry[]
}

function parseLogbookMonth(raw: any): LogbookMonth | null {
  if (!raw || !Array.isArray(raw.entries)) return null
  return { entries: raw.entries.map(parseLogbookEntry).filter((e: LogbookEntry | null): e is LogbookEntry => !!e) }
}

export interface CompleteResult {
  /** Puts the task back as it was and drops the Logbook entry, if any. */
  undo: () => void
  /** For a repeating task, the next date it moved to. */
  nextDay: string | null
}

export const useDocketStore = defineStore('docket', () => {
  const settings = useSettingsStore()
  const corpus = useCorpusStore()
  const log = useWritingLogStore()
  const device = deviceId()

  const taskRows = createSyncedRows<DocketTask>({ storageKey: 'ink-docket-tasks', remotePrefix: 'docket.task.', parse: parseTask })
  const listRows = createSyncedRows<DocketList>({ storageKey: 'ink-docket-lists', remotePrefix: 'docket.list.', parse: parseList })
  const logbookRows = createSyncedRows<LogbookMonth>({ storageKey: 'ink-docket-logbook', remotePrefix: 'docket.done.', parse: parseLogbookMonth })
  // Milestones as the Goals tool kept them. Read only to move them into
  // Docket; see migrateMilestones.
  const legacyMilestoneRows = createSyncedRows<any>({
    storageKey: 'ink-goal-milestones',
    remotePrefix: 'goal.milestone.',
    parse: (raw) => (raw && typeof raw === 'object' ? raw : null),
  })

  const dayOf = (ms: number) => writingDayKey(ms, settings.goalDays.dayStartHour)

  // ── Reading ─────────────────────────────────────────────────────────────
  const tasks = computed(() => Object.values(taskRows.values.value).sort((a, b) => a.order - b.order || a.createdAt - b.createdAt))
  const allLists = computed(() => Object.values(listRows.values.value).sort((a, b) => a.order - b.order || a.name.localeCompare(b.name)))
  const lists = computed(() => allLists.value.filter((list) => !list.archived))

  function findTask(id: string): DocketTask | null {
    return taskRows.values.value[id] ?? null
  }

  function findList(id: string | null): DocketList | null {
    return id ? listRows.values.value[id] ?? null : null
  }

  /**
   * Whether a task belongs to a scope (ids inside a desk or folder; null for
   * everything): by its link, else by its list's desk. Tasks tied to nothing,
   * or to something since deleted, belong everywhere.
   */
  function inScope(task: Pick<DocketTask, 'linkedId' | 'listId'>, ids: Set<string> | null): boolean {
    if (!ids) return true
    if (task.linkedId && corpus.findNode(task.linkedId)) return ids.has(task.linkedId)
    const deskId = findList(task.listId)?.deskId
    return deskId && corpus.findNode(deskId) ? ids.has(deskId) : true
  }

  /** Every finished task: the Logbook, then finished rows not yet moved there. Newest first. */
  const logbook = computed<LogbookEntry[]>(() => {
    const seen = new Set<string>()
    const entries: LogbookEntry[] = []
    const add = (entry: LogbookEntry) => {
      const key = `${entry.taskId}@${entry.doneAt}`
      if (seen.has(key)) return // Two devices moved the same task.
      seen.add(key)
      entries.push(entry)
    }
    for (const month of Object.values(logbookRows.values.value)) month.entries.forEach(add)
    for (const task of tasks.value) {
      if (!task.done || task.doneAt === null) continue
      add({ taskId: task.id, title: task.title, listId: task.listId, linkedId: task.linkedId, day: dayOf(task.doneAt), doneAt: task.doneAt })
    }
    return entries.sort((a, b) => b.doneAt - a.doneAt)
  })

  // ── Tasks ─────────────────────────────────────────────────────────────────
  function nextOrder(listId: string | null): number {
    return tasks.value.filter((task) => task.listId === listId).reduce((max, task) => Math.max(max, task.order + 1), 0)
  }

  function createId(): string {
    return crypto.randomUUID()
  }

  /** Adds a task at the end of its list. Returns it, or null without a title. */
  function addTask(patch: Partial<DocketTask>): DocketTask | null {
    const title = (patch.title ?? '').trim()
    if (!title) return null
    const task = parseTask(blankTask(createId(), Date.now(), { ...patch, title, order: patch.order ?? nextOrder(patch.listId ?? null) }))
    if (!task) return null
    taskRows.write(task.id, task)
    return task
  }

  function saveTask(task: DocketTask) {
    const parsed = parseTask(task)
    if (parsed) taskRows.write(parsed.id, parsed)
  }

  /** Deletes a task; the returned function puts it back. */
  function removeTask(id: string): () => void {
    const prior = findTask(id)
    if (!prior) return () => {}
    taskRows.write(id, null)
    return () => saveTask(prior)
  }

  function appendLogbook(entry: LogbookEntry) {
    const rowId = `${device}.${entry.day.slice(0, 7)}`
    const current = logbookRows.values.value[rowId] ?? { entries: [] }
    logbookRows.write(rowId, { entries: [...current.entries, entry] })
  }

  function dropLogbook(entry: LogbookEntry) {
    const rowId = `${device}.${entry.day.slice(0, 7)}`
    const current = logbookRows.values.value[rowId]
    if (!current) return
    logbookRows.write(rowId, {
      entries: current.entries.filter((e) => !(e.taskId === entry.taskId && e.doneAt === entry.doneAt)),
    })
  }

  /**
   * Finishes a task. A repeating one moves to its next date with its steps
   * unticked, and the finish goes straight to the Logbook. A task in a
   * checklist stays in place, crossed out.
   */
  function completeTask(id: string, now = Date.now()): CompleteResult | null {
    const task = findTask(id)
    if (!task || task.done) return null
    if (task.repeat) {
      const finishedOn = dayOf(now)
      const entry: LogbookEntry = { taskId: task.id, title: task.title, listId: task.listId, linkedId: task.linkedId, day: finishedOn, doneAt: now }
      // The writer may keep repeats' finishes out of the Logbook (Settings > Docket).
      const logged = settings.docketPrefs.logRepeats
      if (logged) appendLogbook(entry)
      // An event's prep task goes on to the event's next occurrence, or is done when there's none.
      const next = task.repeat.kind === 'event'
        ? nextPrepDates(task, useCalendarStore().events, finishedOn) ?? { done: true, doneAt: now }
        : nextTaskDates(task, finishedOn)
      saveTask({ ...task, ...next, steps: task.steps.map((step) => ({ ...step, done: false })) })
      const nextDay = 'done' in next ? null : (next.doOn ?? ('dueOn' in next ? next.dueOn : null))
      return {
        nextDay,
        undo: () => {
          if (logged) dropLogbook(entry)
          saveTask(task)
        },
      }
    }
    saveTask({ ...task, done: true, doneAt: now })
    return { nextDay: null, undo: () => saveTask(task) }
  }

  function reopenTask(id: string) {
    const task = findTask(id)
    if (task?.done) saveTask({ ...task, done: false, doneAt: null })
  }

  /** Ticks or unticks a step. Ticking the last one finishes the task, when the writer wants that. */
  function toggleStep(taskId: string, stepId: string): CompleteResult | null {
    const task = findTask(taskId)
    if (!task) return null
    const steps = task.steps.map((step) => (step.id === stepId ? { ...step, done: !step.done } : step))
    saveTask({ ...task, steps })
    const ticked = steps.find((step) => step.id === stepId)?.done
    if (ticked && settings.docketPrefs.finishWithSteps && !task.done && steps.every((step) => step.done)) return completeTask(taskId)
    return null
  }

  /** Puts tasks in the given order; their list is unchanged. */
  function reorderTasks(ids: string[]) {
    ids.forEach((id, index) => {
      const task = findTask(id)
      if (task && task.order !== index) taskRows.write(id, { ...task, order: index })
    })
  }

  /** Moves a task to another list, at its end. */
  function moveTask(id: string, listId: string | null) {
    const task = findTask(id)
    if (task && task.listId !== listId) saveTask({ ...task, listId, order: nextOrder(listId) })
  }

  // ── Lists ─────────────────────────────────────────────────────────────────
  function nextListOrder(): number {
    return allLists.value.reduce((max, list) => Math.max(max, list.order + 1), 0)
  }

  function saveList(list: DocketList) {
    const parsed = parseList({ ...list, name: list.name.trim() })
    if (parsed?.name) listRows.write(parsed.id, parsed)
  }

  function addList(patch: Partial<DocketList> & { name: string }): DocketList | null {
    const list = parseList({ color: 'accent', icon: null, deskId: null, checklist: false, archived: false, ...patch, id: patch.id ?? createId(), order: nextListOrder() })
    if (!list?.name.trim()) return null
    listRows.write(list.id, list)
    return list
  }

  /** Deletes a list and every task in it. */
  function removeList(id: string) {
    for (const task of tasks.value) if (task.listId === id) taskRows.write(task.id, null)
    if (listRows.values.value[id]) listRows.write(id, null)
  }

  function reorderLists(ids: string[]) {
    ids.forEach((id, index) => {
      const list = listRows.values.value[id]
      if (list && list.order !== index) listRows.write(id, { ...list, order: index })
    })
  }

  /** Unticks every item in a checklist, for its next use. */
  function resetChecklist(listId: string) {
    for (const task of tasks.value) {
      if (task.listId === listId && task.done) saveTask({ ...task, done: false, doneAt: null })
    }
  }

  // ── Housekeeping ──────────────────────────────────────────────────────────

  /**
   * Moves tasks finished more than two weeks ago into this device's Logbook
   * rows. Checklist items are never moved: they are kept for reuse. Safe to
   * run again: a task another device already moved is skipped when shown.
   */
  function packFinished(now = Date.now()) {
    const cutoff = now - PACK_AFTER_DAYS * DAY_MS
    for (const task of tasks.value) {
      if (!task.done || task.doneAt === null || task.doneAt > cutoff) continue
      if (findList(task.listId)?.checklist) continue
      // Already in the Logbook: the row came back from a device whose clock
      // runs ahead, so only the row needs removing again.
      const logged = Object.values(logbookRows.values.value)
        .some((month) => month.entries.some((e) => e.taskId === task.id && e.doneAt === task.doneAt))
      if (!logged) appendLogbook({ taskId: task.id, title: task.title, listId: task.listId, linkedId: task.linkedId, day: dayOf(task.doneAt), doneAt: task.doneAt })
      taskRows.write(task.id, null)
    }
    pruneLogbook(now)
  }

  /**
   * Drops this device's Logbook months older than the writer keeps
   * (Settings > Docket). Each device prunes only its own rows, since a row
   * only ever has one writer.
   */
  function pruneLogbook(now = Date.now()) {
    const months = settings.docketPrefs.logbookMonths
    if (!months) return
    const cutoff = new Date(now)
    cutoff.setMonth(cutoff.getMonth() - months)
    const oldest = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, '0')}`
    for (const rowId of Object.keys(logbookRows.values.value)) {
      if (rowId.startsWith(`${device}.`) && rowId.slice(device.length + 1) < oldest) logbookRows.write(rowId, null)
    }
  }

  /**
   * The list everyone starts with: an empty Writing list. Made only if it has
   * never existed here (a deleted one leaves a marker), and only after a
   * sync, so a Writing list deleted elsewhere is not brought back.
   */
  function ensureStarterList() {
    if (listRows.rows.value[WRITING_LIST_ID]) return
    listRows.write(WRITING_LIST_ID, {
      id: WRITING_LIST_ID, name: 'Writing', color: 'accent', icon: null, deskId: null, checklist: false, order: 0, archived: false,
    })
  }

  /** Milestones become tasks in a Milestones list, keeping their ids. */
  function migrateMilestones() {
    const legacy = Object.values(legacyMilestoneRows.values.value)
    if (!legacy.length) return
    if (!listRows.rows.value[MILESTONES_LIST_ID]) {
      listRows.write(MILESTONES_LIST_ID, {
        id: MILESTONES_LIST_ID, name: 'Milestones', color: 'accent', icon: null, deskId: null, checklist: false, order: nextListOrder(), archived: false,
      })
    }
    legacy
      .sort((a, b) => (Number(a.createdAt) || 0) - (Number(b.createdAt) || 0))
      .forEach((raw, index) => {
        if (!taskRows.rows.value[raw.id]) {
          const task = taskFromMilestone(raw, index)
          if (task) taskRows.write(task.id, task)
        }
        legacyMilestoneRows.write(raw.id, null)
      })
  }

  watch(() => legacyMilestoneRows.values.value, migrateMilestones, { immediate: true })

  async function syncWithRemote(remote: RemoteSettings) {
    await Promise.all([
      taskRows.syncWithRemote(remote),
      listRows.syncWithRemote(remote),
      logbookRows.syncWithRemote(remote),
      legacyMilestoneRows.syncWithRemote(remote),
    ])
  }

  return {
    tasks,
    lists,
    allLists,
    logbook,
    today: computed(() => log.today),
    findTask,
    findList,
    inScope,
    addTask,
    saveTask,
    removeTask,
    completeTask,
    reopenTask,
    toggleStep,
    reorderTasks,
    moveTask,
    nextOrder,
    saveList,
    addList,
    removeList,
    reorderLists,
    resetChecklist,
    packFinished,
    ensureStarterList,
    createId,
    syncWithRemote,
  }
})
